import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Result } from '../models/result';
import  useSchoolStore  from './schoolStore';
import useUserStore from './userStore';
import useAuthStore from './authStore';
import * as repo from '../repositories/reports/bulkReportRepo';
import * as privilegeRepo from '../repositories/privilegeRepo';
import { Privileges } from '../repositories/privilegeRepo';
import * as utils from '../utils/utils';
import { BulkReportType } from '../models/bulk_report';

export const useBulkReportStore = create(persist((set, get)=>({
    
    bulkReports: null,
    isBulkReportLoading: false,
    isBulkReportCreating: false,
    deletingReportIds: [], // Track which reports are being deleted
    // Error items (for failed bulk reports)
    loadingErrorItemsReportIds: [],
    errorItemsByReportId: {},
    error: null,
    getBulkReports: async () => {
        const {currentUser} = useUserStore.getState();
        const {schoolData} = useSchoolStore.getState();
        const {userToken} = useAuthStore.getState();
        if(!currentUser || !schoolData){
            set({error: `Failed to get user or school info`})
            return;
        }

        ///cehck privilege
        set({isBulkReportLoading: true, error: null});
      if(!utils.isAdminUser(currentUser.type)){
        const result = await privilegeRepo.hasPrivilege({
            schoolId: schoolData.id,
            privilege: Privileges.REPORT_PRIVILEGE,
            userName: currentUser.name,
        });

        if(!result.success){
            set({error: result.message, isBulkReportLoading: false});
            return result;
        } 
      }

        const bulkReportResult = await repo.getBulkReports({
            schoolId: schoolData.id,
            userToken: userToken,
        });

        if(!bulkReportResult.success){
            set({error: bulkReportResult.message, isBulkReportLoading: false});
            return bulkReportResult;
        }
        set({bulkReports: bulkReportResult.value, isBulkReportLoading: false, error: null});
        return bulkReportResult;
    },

    createBulkReport: async (data) => {
        const {classList, reportType} = data;
        const {currentUser} = useUserStore.getState();
        const {schoolData} = useSchoolStore.getState();
        const {userToken} = useAuthStore.getState();

        if(!currentUser || !schoolData || !userToken){
            set({error: `Failed to get user or school info`})
            return;
        }

        if(!classList || !classList.length || !reportType){
            set({error: `Invalid data`})
            return;
        }

          ///cehck privilege
          set({isBulkReportCreating: true});
          if(!utils.isAdminUser(currentUser.type)){
            const result = await privilegeRepo.hasPrivilege({
                schoolId: schoolData.id,
                privilege: Privileges.REPORT_PRIVILEGE,
                userName: currentUser.name,
            });
    
            if(!result.success){
                set({error: result.message, isBulkReportCreating: false});
                return result;
            } 
          }

         if(reportType === BulkReportType.STUDENT_PERFORMANCE){
            
            const result = await repo.createStudentPerformanceBulkReport({
                schoolInfo: schoolData,
                userInfo: currentUser,
                classList,
                academicYear: schoolData.currentAcademicYear,
                userToken: userToken,
                roleToken: currentUser.roleToken,
            })

            if(!result.success){
                set({error: result.message, isBulkReportCreating: false});
                return result;
            }

            set({isBulkReportCreating: false});
            ///Now load the reports again
            get().getBulkReports();
            
         }else{
            set({error: `Invalid report type`, isBulkReportCreating: false});
            return;
         }
    },

    deleteBulkReport: async (reportId) => {
        const {currentUser} = useUserStore.getState();
        const {schoolData} = useSchoolStore.getState();
        const {userToken} = useAuthStore.getState();

        if(!currentUser || !schoolData || !userToken){
            set({error: `Failed to get user or school info`});
            return;
        }

        if(!reportId){
            set({error: `Report ID is required`});
            return;
        }

        // Check privilege
        if(!utils.isAdminUser(currentUser.type)){
            const result = await privilegeRepo.hasPrivilege({
                schoolId: schoolData.id,
                privilege: Privileges.REPORT_PRIVILEGE,
                userName: currentUser.name,
            });

            if(!result.success){
                set({error: result.message});
                return result;
            } 
        }

        // Add reportId to deletingReportIds
        set(state => ({
            deletingReportIds: [...state.deletingReportIds, reportId]
        }));

        try {

            ///get report by id from bulkReports
            const report = get().bulkReports.find(report => report.id === reportId);
            if(!report){
                set({error: `Report not found`});
                return;
            }

            const result = await repo.deleteBulkReport({
                reportId,
                userToken: userToken,
                schoolId: schoolData.id,
                zipFilePath: report.zipPath,
            });


            if(!result.success){
                set(state => ({
                    error: result.message,
                    deletingReportIds: state.deletingReportIds.filter(id => id !== reportId)
                }));
                return result;
            }

            // Remove the deleted report from the list and clear from deletingReportIds
            set(state => ({
                bulkReports: state.bulkReports.filter(report => report.id !== reportId),
                deletingReportIds: state.deletingReportIds.filter(id => id !== reportId)
            }));

            return result;
        } catch (error) {
            set(state => ({
                error: `Error deleting report: ${error.message}`,
                deletingReportIds: state.deletingReportIds.filter(id => id !== reportId)
            }));
        }
    }
    ,
    getReportErrorItems: async (reportId) => {
        const {currentUser} = useUserStore.getState();
        const {schoolData} = useSchoolStore.getState();
        const {userToken} = useAuthStore.getState();

        if(!currentUser || !schoolData || !userToken){
            set({error: `Failed to get user or school info`});
            return;
        }

        if(!reportId){
            set({error: `Report ID is required`});
            return;
        }

        // if already loaded, return cached
        const cached = get().errorItemsByReportId?.[reportId];
        if(cached){
            return new Result({ value: cached });
        }

        // mark loading
        set(state => ({
            loadingErrorItemsReportIds: [...state.loadingErrorItemsReportIds, reportId]
        }));

        try{
            const result = await repo.getReportErrorItems({
                schoolId: schoolData.id,
                reportId,
                userToken,
            });

            if(!result.success){
                set(state => ({
                    error: result.message,
                    loadingErrorItemsReportIds: state.loadingErrorItemsReportIds.filter(id => id !== reportId)
                }));
                return result;
            }

            set(state => ({
                errorItemsByReportId: { ...state.errorItemsByReportId, [reportId]: result.value },
                loadingErrorItemsReportIds: state.loadingErrorItemsReportIds.filter(id => id !== reportId)
            }));

            return result;
        }catch(error){
            set(state => ({
                error: `Error getting report error items: ${error.message}`,
                loadingErrorItemsReportIds: state.loadingErrorItemsReportIds.filter(id => id !== reportId)
            }));
        }
    }
}),
{
    name: 'bulkReportStore',
    partialize: (state) => ({
       
    })
}
))